import styles from './index.module.css'

import { LuExternalLink } from '@react-icons/all-files/lu/LuExternalLink'
import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { clientLibraryDiscoveryPanelFavorite } from '@disco/state-management/actions/client-library-discovery-panel-actions'
import { getTextColor } from '@disco/utils/colors'

import type {
  ClientLibraryDiscoveryPanelItem,
  ClientLibraryId,
} from 'types/client-library'

import { getAuthTransferUrl } from 'utils/routing'

import LibraryArtwork from 'components/common/library-artwork'
import GridItem from 'components/ui/grid-item'
import Icon from 'components/ui/icon'
import Tooltip from 'components/ui/tooltip'

import { useDispatch } from 'hooks/use-redux'

type Props = {
  lib: ClientLibraryDiscoveryPanelItem
  onClick?: (id: ClientLibraryId) => void
  onVisitCatalog: (id: ClientLibraryId) => void
  type?: 'shadow'
}

/**
 * Grid item component for displaying library/catalog information with access status and actions
 * @param lib - Library discovery panel item data
 * @param onClick - Click handler for the grid item
 * @param onVisitCatalog - Called when the "Visit Catalog" external link is activated; required so every surface reports analytics
 * @param type - Visual style type, currently supports 'shadow'
 */
const LibraryGridItem = ({
  lib,
  onClick,
  onVisitCatalog,
  type,
}: Props): null | React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Null-safe derefs: hooks must run before the `if (!lib)` guard below, so
  // nothing here may throw when lib is missing (e.g. a stale keyed lookup)
  const id = lib?.id
  const image = lib?.discovery_image_url || lib?.image_logo_url
  const display_name = lib?.display_name
  const status = lib?.status
  const short_description = lib?.short_description

  // Local state for optimistic UI updates when toggling favorites
  const [isFavorite, setIsFavorite] = useState(lib?.is_favorite)

  // Sync local state with prop when it changes (e.g., after API response)
  useEffect(() => {
    setIsFavorite(lib?.is_favorite)
  }, [lib?.is_favorite])

  // Navigate to catalog inner page with data passed via state
  const handleCatalogClick = useCallback(() => {
    navigate(`/discover/catalog?id=${id}`, {
      state: { catalog: lib },
    })
    if (onClick) {
      onClick(id)
    }
  }, [navigate, id, lib, onClick])
  const favorite = useCallback(() => {
    setIsFavorite(prev => !prev) // Optimistic update
    dispatch(clientLibraryDiscoveryPanelFavorite(id, !isFavorite))
  }, [dispatch, id, isFavorite])

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      favorite()
    },
    [favorite],
  )

  const handleVisitCatalogClick = useCallback(
    (e: React.MouseEvent) => {
      // auxclick fires for any non-primary button, but only middle-click follows the href
      if (e.type === 'auxclick' && e.button !== 1) return
      onVisitCatalog(id)
    },
    [onVisitCatalog, id],
  )

  if (!lib) return null

  // Fallback catalog name if display_name is missing
  const catalogName = display_name || 'Untitled Catalog'
  const description = short_description

  const catalogUrl = getAuthTransferUrl(id)

  // Overlay - icons on artwork
  const overlay: React.ReactNode = (
    <GridItem.Overlay
      actions={
        <div className={styles.libraryGridItemArtworkIcons}>
          {status === 'approved' && (
            <Tooltip content="You have download access to this Catalog.">
              <div className={styles.libraryGridItemArtworkIcon}>
                <Icon color="#fff" type="check" width={15} height={15} />
              </div>
            </Tooltip>
          )}
        </div>
      }
    />
  )

  const media: React.ReactNode = (
    <LibraryArtwork
      color={getTextColor(display_name)}
      image={image}
      overlay={overlay}
      ratio={1}
      type={type}
      noPadding
    />
  )

  // Title content: Catalog name, Description
  const title = (
    <div className={styles.libraryGridItemContent}>
      <div className={styles.libraryGridItemTitle}>{catalogName}</div>
      <div className={styles.libraryGridItemDescription}>{description}</div>
    </div>
  )

  const actions = (
    <div className={styles.libraryGridItemActions}>
      <button
        type="button"
        className={styles.libraryGridItemFavoriteButton}
        onClick={handleFavoriteClick}
        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        <Icon
          type="heart"
          width={20}
          height={20}
          className={clsx(styles.libraryGridItemFavoriteIcon, {
            [styles.libraryGridItemFavoriteIconFilled]: isFavorite,
            [styles.libraryGridItemFavoriteIconNotFilled]: !isFavorite,
          })}
        />
      </button>

      <Tooltip content="Visit Catalog">
        <a
          href={catalogUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.libraryGridItemVisitButton}
          onClick={handleVisitCatalogClick}
          onAuxClick={handleVisitCatalogClick}
          aria-label="Visit Catalog"
        >
          <LuExternalLink
            size={18}
            className={styles.libraryGridItemVisitIcon}
          />
        </a>
      </Tooltip>
    </div>
  )

  return (
    <div
      className={clsx(
        styles.libraryGridItemWrapper,
        styles.libraryGridItemRedesigned,
      )}
    >
      <GridItem media={media} title={title} onClick={handleCatalogClick} />
      {actions}
    </div>
  )
}

export default LibraryGridItem
